/**
 * AssetsTopicMatrix — "View by Topic" swimlane. Same VERTICAL CSS-Grid layout as
 * AssetsIndustryMatrix (columns = topics instead of industries; row 1 = topic
 * headers; each column stacks that topic's asset cards). Sharing the .ivt-assets-
 * matrix grid means topic cards get the exact same width (minmax(150px,1fr)) and
 * equal-height rows (1fr) as by-Industry cards — the two views are pixel-identical
 * apart from the header label + the card's sub-topic line.
 *
 * Topic-anchored assets only (byTopic excludes industry-anchored ones), so unlike
 * the industry universe there are no forced-empty columns — a topic column exists
 * iff it has assets.
 */
import type { TopicRow } from '../utils/assetGrouping';
import TopicAssetCard from './TopicAssetCard';

export default function AssetsTopicMatrix({ rows }: { rows: TopicRow[] }) {
  if (rows.length === 0) {
    return <div className="ivt-assets-matrix__empty">没有符合条件的 Topic</div>;
  }
  const maxRows = Math.max(...rows.map((r) => r.assets.length));

  return (
    <div
      className="ivt-assets-matrix"
      style={{
        // Fixed max column width (not 1fr) so a SINGLE topic doesn't stretch its
        // card across the whole row (would look like a banner). Columns pack from
        // the left; many topics overflow-x scroll.
        gridTemplateColumns: `repeat(${rows.length}, minmax(150px, 250px))`,
        gridTemplateRows: `auto repeat(${maxRows}, 1fr)`,
        justifyContent: 'start',
      }}
    >
      {/* Row 1: topic column headers */}
      {rows.map((row, ci) => (
        <div
          className="ivt-assets-matrix__colhead"
          key={`h-${row.topic}`}
          style={{ gridColumn: ci + 1, gridRow: 1 }}
        >
          {row.topic} <span className="ivt-assets-matrix__count">({row.assets.length})</span>
        </div>
      ))}

      {/* Cards placed by (column, row); empty cells left blank so rows stay aligned. */}
      {rows.map((row, ci) =>
        row.assets.map((a, ri) => (
          <div
            className="ivt-assets-matrix__cell"
            key={a.assetId}
            style={{ gridColumn: ci + 1, gridRow: ri + 2 }}
          >
            <TopicAssetCard asset={a} />
          </div>
        )),
      )}
    </div>
  );
}
